import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { ErrorDisplayingFallback } from "@/features/ErrorHandling/components/ErrorDisplayingFallback/ErrorDisplayingFallback";
import { PlatformSpecificErrorBoundary } from "@/features/ErrorHandling/components/PlatformSpecificErrorBoundary";
import { FeaturedEvents } from "@/features/EventsGrid/components/FeaturedEvents/FeaturedEvents";
import { FeaturedEventsDepartment } from "@/features/EventsGrid/components/FeaturedEvents/FeaturedEventsDepartment";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import {
  NewPatronEventsListQuery,
  NewPatronEventsListQuery$variables,
} from "./__generated__/NewPatronEventsListQuery.graphql";
import { UpcomingEventsClub } from "./components/UpcomingEventsClub";
import { UpcomingEventsDepartment } from "./components/UpcomingEventsDepartment";
import { UpcomingEventsSkeleton } from "./components/UpcomingEventsSkeleton";

const EVENTS_PAGE_SIZE = 20;

export const NewPatronEventsList = ({
  memberId,
  departmentOrClubId,
  search,
  dateRange,
  selectedTags,
}: {
  memberId?: string;
  departmentOrClubId?: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  search: string;
  selectedTags: string[];
}) => {
  const [newPatronEventsListQueryRef, loadNewPatronEventsListQuery] =
    useQueryLoader<NewPatronEventsListQuery>(NewPatronEventsListQueryString);

  useEffect(() => {
    if (departmentOrClubId) {
      const queryParams: NewPatronEventsListQuery$variables = {
        count: EVENTS_PAGE_SIZE,
        id: departmentOrClubId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        search,
      };

      if (selectedTags.length > 0) {
        queryParams.eventTags = selectedTags;
      }

      if (memberId) {
        queryParams.memberId = memberId;
      }

      loadNewPatronEventsListQuery(queryParams, {
        fetchPolicy: "store-and-network",
      });
    }
  }, [
    loadNewPatronEventsListQuery,
    departmentOrClubId,
    memberId,
    dateRange.startDate,
    dateRange.endDate,
    selectedTags,
    search,
  ]);

  return (
    <Suspense fallback={<Loader />}>
      {newPatronEventsListQueryRef ? (
        <NewPatronEventsListInternal queryRef={newPatronEventsListQueryRef} />
      ) : null}
    </Suspense>
  );
};

const NewPatronEventsListInternal = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<NewPatronEventsListQuery>;
}) => {
  const data = usePreloadedQuery(NewPatronEventsListQueryString, queryRef);
  const isClub = data.node?.__typename === "Club";
  const isDepartment = data.node?.__typename === "Department";

  const ListHeaderComponent = useMemo(() => {
    return (
      <>
        {data.node && isClub ? (
          <FeaturedEvents nodeRef={data.node} />
        ) : isDepartment ? (
          <FeaturedEventsDepartment nodeRef={data.node} />
        ) : null}
      </>
    );
  }, [data.node, isClub, isDepartment]);

  return (
    <Box bg="surface-primary" p={6} flex={1} testID="new-patron-events-list">
      <PlatformSpecificErrorBoundary
        fallback={(eventId, error, resetError) => (
          <ErrorDisplayingFallback
            error={error}
            eventId={eventId}
            resetError={resetError}
          />
        )}
        children={
          <Suspense fallback={<UpcomingEventsSkeleton />}>
            {data.node && isClub ? (
              <UpcomingEventsClub
                ListHeaderComponent={<>{ListHeaderComponent}</>}
                fragmentRef={data.node}
              />
            ) : data.node && isDepartment ? (
              <UpcomingEventsDepartment
                ListHeaderComponent={<>{ListHeaderComponent}</>}
                fragmentRef={data.node}
              />
            ) : null}
          </Suspense>
        }
      />
    </Box>
  );
};

export const NewPatronEventsListQueryString = graphql`
  query NewPatronEventsListQuery(
    $id: ID!
    $count: Int!
    $cursor: String
    $memberId: ID
    $startDate: Date
    $endDate: Date
    $eventTags: [ID]
    $search: String
  ) {
    node(id: $id) {
      id
      __typename
      ... on Club {
        ...FeaturedEventsFragment
        ...UpcomingEventsClub_club
      }
      ... on Department {
        ...FeaturedEventsDepartmentFragment
        ...UpcomingEventsDepartment_department
      }
    }
  }
`;
